import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import { APIDataIntegration } from '@evertel/types'
import { Badge, Col, Icon, InfoBox, Row, Spinner, Text, ToggleSwitch } from '@evertel/web/ui'
import { ProfilePic } from '@evertel/web/user'
import { RoomContext } from '../../RoomContext'
import { DisableConnectorModal, EnableConnectorModal } from '../modals'

const RoomConnectors: React.FC = observer(() => {

    const {
        roomController,
        roomConnectorsController,
        asideOpenComplete,
        canManageRoom
    } = useContext(RoomContext)

    const room = roomController.room

    const [pendingEnableConnector, setPendingEnableConnector] = useState<APIDataIntegration | null>(null)
    const [pendingDisableConnector, setPendingDisableConnector] = useState<APIDataIntegration | null>(null)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (room?.id) {
            setIsLoading(true)
            roomConnectorsController.fetchDepartmentIntegrations()
                .then(() => roomConnectorsController.fetchActiveKeyCounts())
                .finally(() => setIsLoading(false))
        }
    }, [room?.id])

    if (!room?.id || !asideOpenComplete) return null

    const { enabledConnectors, availableConnectors } = roomConnectorsController

    const allEnabledInactive = enabledConnectors.length > 0 &&
        enabledConnectors.every(c => !roomConnectorsController.hasActiveKeys(c.id))

    const handleToggle = (connector: APIDataIntegration, enabled: boolean) => {
        if (enabled) {
            setPendingDisableConnector(connector)
        } else {
            setPendingEnableConnector(connector)
        }
    }

    const handleConfirmEnable = async () => {
        if (!pendingEnableConnector) return
        await roomConnectorsController.toggleConnector(pendingEnableConnector.id)
        setPendingEnableConnector(null)
    }

    const handleConfirmDisable = async () => {
        if (!pendingDisableConnector) return
        await roomConnectorsController.toggleConnector(pendingDisableConnector.id)
        setPendingDisableConnector(null)
    }

    return (
        <>
            {isLoading ? (
                <Row align="center" className="py-5">
                    <Spinner />
                </Row>
            ) : (
                <>
                    <Text tag="h5" bold className="mb-3">
                        Enabled Connectors
                    </Text>

                    {enabledConnectors.length === 0 ? (
                        <Row className="d-flex align-items-center justify-content-start border rounded p-3">
                            <small className="fw-bold mb-0">There are no connectors currently enabled in this room.</small>
                        </Row>
                    ) : (
                        enabledConnectors.map(connector => (
                            <ConnectorRow
                                key={connector.id}
                                connector={connector}
                                enabled={true}
                                hasActiveKeys={roomConnectorsController.hasActiveKeys(connector.id)}
                                toggling={roomConnectorsController.isToggling(connector.id)}
                                disabled={!canManageRoom}
                                onToggle={() => handleToggle(connector, true)}
                            />
                        ))
                    )}

                    {allEnabledInactive && (
                        <InfoBox color="warning" className="mt-3">
                            <strong>No active access keys detected.</strong>
                            <div>Your enabled connector{enabledConnectors.length > 1 ? 's' : ''} won't receive any data until at least one access key is created. Visit <strong>Connector Configuration</strong> to get set up.</div>
                        </InfoBox>
                    )}

                    <Text tag="h5" bold className="mt-4 mb-3">
                        Available Connectors
                    </Text>

                    {availableConnectors.length === 0 ? (
                        <Row className="d-flex align-items-center justify-content-center border rounded p-3">
                            <small className="fw-bold mb-0">There are no connectors currently configured for your agency.</small>
                        </Row>
                    ) : (
                        availableConnectors.map(connector => (
                            <ConnectorRow
                                key={connector.id}
                                connector={connector}
                                enabled={false}
                                hasActiveKeys={roomConnectorsController.hasActiveKeys(connector.id)}
                                toggling={roomConnectorsController.isToggling(connector.id)}
                                disabled={!canManageRoom}
                                onToggle={() => handleToggle(connector, false)}
                            />
                        ))
                    )}
                </>
            )}

            <EnableConnectorModal
                visible={!!pendingEnableConnector}
                connectorName={pendingEnableConnector?.name || ''}
                loading={pendingEnableConnector ? roomConnectorsController.isToggling(pendingEnableConnector.id) : false}
                onClose={() => setPendingEnableConnector(null)}
                onConfirm={handleConfirmEnable}
            />

            <DisableConnectorModal
                visible={!!pendingDisableConnector}
                loading={pendingDisableConnector ? roomConnectorsController.isToggling(pendingDisableConnector.id) : false}
                onClose={() => setPendingDisableConnector(null)}
                onConfirm={handleConfirmDisable}
            />
        </>
    )
})

interface ConnectorRowProps {
    connector: APIDataIntegration
    enabled: boolean
    hasActiveKeys: boolean
    toggling: boolean
    disabled: boolean
    onToggle: () => void
}

const ConnectorRow: React.FC<ConnectorRowProps> = observer(({
    connector,
    enabled,
    hasActiveKeys,
    toggling,
    disabled,
    onToggle
}) => (
    <Row className="py-3 px-3 mb-2 border rounded align-items-start px-2">
        <div className="me-3 d-flex flex-column align-items-center">
            <ProfilePic
                userId={connector.integrationUserId}
                className="connector-icon"
                width={45}
                height={45}
            />
            <Badge color={hasActiveKeys ? 'success' : 'muted'} className="mt-3 d-inline-flex justify-content-center" style={{width: 55}}>
                <span>{hasActiveKeys ? 'Active' : 'Inactive'}</span>
            </Badge>
        </div>
        <Col>
            <Text className="text-break" bold>
                {connector.name}
            </Text>
            {connector.description && (
                <Text size="smaller" color="muted">
                    {connector.description}
                </Text>
            )}
        </Col>
        <Col className="ms-2" xs="auto" align="right">
            {toggling ? (
                <Spinner width={24} />
            ) : (
                <ToggleSwitch
                    name={`connector-${connector.id}`}
                    id={`connector-${connector.id}`}
                    checked={enabled}
                    onChange={onToggle}
                    disabled={disabled}
                    optionLabels={['ON', 'OFF']}
                />
            )}
        </Col>
    </Row>
))

RoomConnectors.displayName = 'RoomConnectors'

export { RoomConnectors }
